const week6 = {
  number: 6,
  title: "Articulation, Anchors, Rules",
  theme: "The tools of Directional Identity",
  modules: [
    {
      id: "6.1",
      title: "Articulation",
      content: `**Articulation is the internal act of making something precise enough to navigate by.**

This is not the same as expression. Expression is communication to others. Articulation is how you tell yourself the truth—how you move from vague sensation to actionable clarity.

Consider the difference:

- You feel bad. (Sensation, unarticulated)
- You feel anxious about something. (Slightly articulated)
- You feel anxious about the meeting tomorrow. (More articulated)
- You feel anxious about the meeting because you are afraid you will be criticized in front of others, and the Trust Resident is activated because this resembles a pattern from childhood when you were humiliated publicly. (Fully articulated)

The sensation is the same. What changes is the precision—how clearly you have named the internal reality.

**Why does articulation matter?**

Because you cannot navigate what you cannot see.

An unarticulated sensation is like fog. You know something is there, but you cannot tell its shape, its source, or what it means. You react to the fog without understanding it.

An articulated state is like a map. Not a complete map—the null hypothesis still holds—but enough clarity to take a step in a direction.

**Articulation is not analysis.**

Analysis happens at a distance. You examine something objectively, from outside.

Articulation happens from inside. You name what you are experiencing as you experience it. You do not step outside the sensation; you find words for it while remaining in it.

This is harder than it sounds. The pull toward analysis—toward getting above the experience, understanding it from a safe distance—is strong. But analysis often removes you from the very data you need. Articulation requires staying in the experience while developing language for it.

**Articulation is a skill.**

Some people are naturally more articulate about their inner states. But everyone can develop this capacity. Through practice, through attention, through the willingness to stay with sensation until words emerge, articulation improves.

The goal is not perfect articulation. The goal is enough articulation to enable the next step.`
    },
    {
      id: "6.2",
      title: "The Three Levels of Articulation",
      content: `Articulation operates at three levels, each building on the last:

**Level 1: Emotional Articulation**

Naming the raw internal state. What is the feeling? What is the sensation?

"I am angry."
"I am scared of losing something important."
"I feel trapped."
"Something is wrong but I don't know what."

Emotional articulation does not require understanding—only honest naming. Many people skip this level, moving straight to explanation or action. But the feeling itself is data. Before you understand why you feel something, you must acknowledge that you feel it.

Emotional articulation is harder than it seems because:
- Feelings are often multiple (anger and fear, hope and grief)
- Feelings are often unwanted (you do not want to feel jealous, so you do not name it)
- Feelings are often unclear (something is happening, but what?)

The practice is simple but requires discipline: stop and name the feeling before doing anything else.

**Level 2: Structural Articulation**

Identifying which residents are active and what conflicts are occurring.

"Trust is activated—I feel unsafe."
"Autonomy and Intimacy are in conflict—I want closeness and I want space."
"The Basement has taken over—that's why I can't think clearly."
"Industry wants to work, but Identity is questioning whether this is the right work."

Structural articulation adds a layer: not just what you feel, but where it is coming from and how different parts of you are relating to each other.

This level requires knowledge of your own architecture. You must have some map of your Inner House—some understanding of the residents and their patterns—before you can articulate structurally.

**Level 3: Directional Articulation**

Revealing where the mind wants to move.

"Something in me is turning toward reinvention."
"I am being pulled away from this relationship, even though I don't want to be."
"I need to move toward rest, even though Industry objects."
"My direction is toward creating something, but I don't yet know what."

Directional articulation is the deepest level. It names not just what you feel or where it comes from, but where you are trying to go. It connects present experience to navigational intent.

**All three levels matter.** Emotional articulation without structure is incomplete. Structural articulation without direction is sterile. The full practice moves through all three: What do I feel? Where is it coming from? Where am I trying to go?`
    },
    {
      id: "6.3",
      title: "Anchors",
      content: `Direction provides the pull. Articulation provides the clarity. But movement still requires stability.

**Anchors are structures—internal or external—that hold the mind in place long enough for direction to take root.**

Consider a ship. Direction tells it where to go. Navigation skills allow it to read wind and water. But when storms come, the ship needs anchors. Without them, it is pushed off course—or smashed against rocks.

Your mind works the same way. You have a direction. You can articulate your state. But when life becomes turbulent—when the Basement activates, when capacity drops, when unexpected events shake the house—you need something that holds you in place.

Anchors are not goals or achievements. They are stabilizing structures that prevent drift.

**Four types of anchors:**

**Structural Anchors** — Stabilize the environment
- Predictable spaces where you feel safe
- Consistent routines that create rhythm
- Financial stability that removes survival anxiety
- A home that holds you

**Behavioral Anchors** — Stabilize the body
- Sleep patterns that are protected
- Physical movement that is non-negotiable
- Nutrition routines that maintain capacity
- Breath practices or meditation

**Emotional Anchors** — Stabilize the residents
- Reliable connections with specific people
- Grounding relationships where you are known
- Honest internal dialogue that you maintain
- Practices that calm the Basement

**Identity Anchors** — Stabilize the narrative
- Core values that you return to
- Personal principles that do not bend
- Chosen roles that remain consistent
- Long-term commitments that define you

**Anchors create stability, but they are not constraints.** A ship at anchor can still swing with the tide. It is held, but it is not frozen. Anchors allow flexibility within a range while preventing catastrophic drift.

When someone loses their anchors—through major life change, through crisis, through collapse—they become disoriented. They may still have direction, but they cannot hold position long enough to move toward it. The first work of recovery is often re-establishing anchors.`
    },
    {
      id: "6.4",
      title: "True Anchors vs. False Anchors",
      content: `Not everything that stabilizes is a true anchor.

**False Anchors** are stabilizers that are not healthy—they create temporary stability at the cost of navigation itself.

Compulsions are false anchors. The ritual that must be performed, the behavior that cannot be stopped—these stabilize in the moment by narrowing the world. They provide predictability, but the predictability comes from eliminating possibility.

Addictions are false anchors. The substance, the behavior, the pattern that reliably changes internal state—these stabilize by bypassing the system rather than supporting it. They provide escape from turbulence, but they do not allow navigation through it.

Destructive relationships are false anchors. The connection that provides stability through dysfunction—codependence, control, enmeshment—these hold you in place, but they hold you in a place that prevents growth.

Rigid routines are false anchors. Routines become false anchors when they are maintained not because they serve navigation but because any deviation creates intolerable anxiety. The routine is no longer a support; it is a cage.

**How to distinguish true anchors from false ones:**

**True anchors expand capacity; false anchors restrict it.** A true anchor gives you stability that allows you to take risks, move in your direction, grow. A false anchor provides stability by eliminating risk, movement, and growth.

**True anchors support direction; false anchors substitute for it.** A true anchor holds you while you figure out where you are going. A false anchor becomes the only thing—there is no direction, only the maintenance of the anchor itself.

**True anchors can be adjusted; false anchors cannot be touched.** A true anchor can be modified as circumstances change. A false anchor is defended with disproportionate intensity—any suggestion of change creates panic.

**True anchors feel like ground; false anchors feel like grip.** A true anchor is something you stand on, stable beneath you. A false anchor is something you cling to, afraid of what happens if you let go.

Examining your anchors honestly requires courage. Some things you rely on for stability may be false anchors. This does not mean they must be eliminated immediately—but it does mean they are not serving navigation.`
    },
    {
      id: "6.5",
      title: "The Rules Framework",
      content: `Direction provides the pull. Articulation provides the clarity. Anchors provide the stability.

**Rules provide the commitments.**

Rules are agreements the mind makes with itself so the house can function. They are structural commitments that keep the mind aligned with its own direction—the boundaries that make internal freedom possible.

This may seem paradoxical. How do rules create freedom? But consider: without any rules, every decision must be made from scratch. Every situation requires complete deliberation. Every moment is exhausting because nothing is settled.

Rules settle things. They take certain decisions off the table. They create predictability within the system. And this predictability frees energy for the decisions that actually matter.

**Three types of rules:**

**Boundary Rules** — Protect the house by determining what is not allowed.

"I do not tolerate relationships that destabilize Trust."
"I do not allow work to invade sleep."
"I do not engage with people who consistently violate my autonomy."

Boundary Rules are about what you keep out. They define the perimeter of the self—what is allowed to enter and what is not. Without Boundary Rules, you are invaded by whatever arrives.

**Behavioral Rules** — Sustain direction by defining behaviors that must be maintained.

"I articulate before reacting."
"I move my body daily."
"I review the week before beginning the next one."

Behavioral Rules are about what you do. They translate direction into consistent action. Without Behavioral Rules, direction remains theoretical—a nice idea that does not produce movement.

**Identity Rules** — The deepest rules: commitments about who you are willing to be.

"I refuse to abandon myself to keep the peace."
"I will not betray my values for comfort."
"I am someone who tells the truth, especially when it is difficult."

Identity Rules are about who you are. They define the self that is navigating—what you will not compromise, what you will not become, what lines you will not cross.

**Rules are not permanent.** They can be revised when direction changes, when circumstances change, when you learn something that makes an old rule obsolete. But they are not abandoned casually. They are commitments—and commitments that shift too easily are not commitments at all.

The question is not whether you have rules. You do—even if you have not articulated them. The question is whether you have chosen your rules consciously or inherited them unconsciously, whether they serve your direction or constrain it.`
    }
  ],
  reflection: {
    prompt: "Name one true anchor in your life—something that genuinely holds you without restricting you. Name one rule you've made with yourself that you actually keep. What do these reveal about your direction?",
    placeholder: "Consider what actually stabilizes you and what commitments you maintain even when no one is watching. These are the structures that enable your navigation."
  }
};

export default week6;
